import { AsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as ClearFilter } from '../../../assets/icons/filter-close.svg';
import { ReactComponent as Filter } from '../../../assets/icons/filter.svg';
import { ReactComponent as ResetButton } from '../../../assets/icons/reset-button.svg';
import { ReactComponent as LargeFontIcon } from '../../../assets/icons/table-component/large-font-icon.svg';
import { ReactComponent as MediumFontIcon } from '../../../assets/icons/table-component/medium-font-icon.svg';
import { ReactComponent as SmallFontIcon } from '../../../assets/icons/table-component/small-font-icon.svg';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import { useAppDispatch } from '../../../redux/hooks';
import MyButton from '../../button/Button';
import CustomPopover from '../../custom-pop-over/CustomPopover';
import ExpandSearchBar from '../../expand-search-bar/ExpandSearchBar';
import ExportButton from '../../export-button/ExportButton';
import TableFieldAllow from '../../table-field-allow/TableFieldAllow';

import CustomTableFilterV2 from './TableFiltersV2';

interface CustomTableProperties {
  currentFontSize?: string;
  sizeChangeButtonRequired?: boolean;
  sizeChangeFunction?: () => void;
  rightTopContent?: JSX.Element;
  leftTopContent?: JSX.Element;
  filters?: CommonFilterProperties[];
  filtersToDispatch?: (payload: FilterUpdateProperties) => PayloadAction<FilterUpdateProperties>;
  filterListLoading: boolean;
  filterListToDispatch?: AsyncThunk<any, any, any>;
  exportDataPdfOrCSV?: (type: 'pdf' | 'csv') => void;
  exportData?: any[];
  exportLoader?: boolean;
  labelForHideColumn?: {
    label: string;
    accessorKey: any;
    disableHideFunc: boolean;
  }[];
  hidedColumn?: string[];
  setHidedColumn?: React.Dispatch<React.SetStateAction<string[]>>;
  refreshCurrentPages?: () => void;
  onClearFilterChange?: () => void;
  flexDirection?: boolean;
}

/**
 * Function to handle table filters
 * @returns
 */
function CustomTableHeaderV2(properties: CustomTableProperties) {
  const {
    currentFontSize,
    sizeChangeButtonRequired,
    rightTopContent,
    leftTopContent,
    sizeChangeFunction,
    filters,
    filterListLoading,
    filtersToDispatch,
    filterListToDispatch,
    exportDataPdfOrCSV,
    exportData,
    exportLoader,
    labelForHideColumn,
    hidedColumn,
    setHidedColumn,
    refreshCurrentPages,
    onClearFilterChange,
    flexDirection,
  } = properties;

  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('customTable');

  // As of now there should be only one search by text filter implemented
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [valueToShow, setValueToShow] = useState<string | number>('');
  const [searchFilter, setSearchFilter] = useState<CommonFilterProperties>();

  /**
   * Function to count applied filters (excluding search filter)
   * @returns number of applied filters
   */
  const countAppliedFilters = (): number => {
    if (!filters) return 0;

    return filters.filter((filter) => {
      // Skip search filter
      if (filter.searchBy) return false;

      // Count filters with applyFilter true
      if (filter.applyFilter) {
        // For date/time range filters
        if (filter.key === filterType.DATE_RANGE || filter.key === filterType.TIME_RANGE) {
          return true;
        }
        // For angle brackets filters
        if (filter.key === filterType.ANGLE_BRACKETS && filter?.value1 && filter?.value2) {
          return true;
        }
        // For multi-select filters
        if (Array.isArray(filter?.value) && filter.type === filterType.MULTISELECT) {
          return filter.value.length > 0;
        }
        // For array value filters
        if (Array.isArray(filter.value)) {
          return filter.value.length > 0;
        }
        // For string value filters
        if (typeof filter.value === 'string') {
          return filter.value !== '';
        }
      }
      return false;
    }).length;
  };
  /**
   * Function to decide font change to show
   * @returns JSX.Element as an svg icon
   */
  const handleFontChangeIcon = (): JSX.Element => {
    let icon;
    switch (currentFontSize) {
      case 'large':
        icon = <LargeFontIcon />;
        break;
      case 'medium':
        icon = <MediumFontIcon />;
        break;
      default:
        icon = <SmallFontIcon />;
        break;
    }
    return icon;
  };

  /**
   * Function to remove filter from the list of selected filters
   * @param key - key of the filter to remove
   */
  const handleRemoveFilterByKey = (key: string) => {
    if (filtersToDispatch) {
      dispatch(
        filtersToDispatch({
          key,
          value: '', // Empty value as we are using same dispatch function for filter updates
          type: 'remove',
        }),
      );
    }
  };

  /**
   * Function to handle size change function
   * @returns {void}
   */
  const handleChangeFilter = () => {
    if (!sizeChangeFunction) return;
    sizeChangeFunction();
  };

  /**
   * Function to remove filter from the list of selected filters
   */
  const handleClearFilters = () => {
    if (filtersToDispatch) {
      dispatch(
        filtersToDispatch({
          key: '', // Empty key as we are using same dispatch function for filter updates
          value: '', // Empty value as we are using same dispatch function for filter updates
          type: 'clear',
        }),
      );
    }
    if (onClearFilterChange) {
      onClearFilterChange();
    }
  };

  /**
   * Function to dispatch the updated filters
   * @param name - name of the filter
   * @param value - value of the filter
   */
  const handleChangeFilters = (name: string, value: string | number) => {
    setValueToShow(value);
  };

  /**
   * Function to update the search filter
   */
  useEffect(() => {
    if (filters) {
      const searchBy = filters.find((filter) => filter.searchBy);
      setSearchFilter(searchBy);
      setValueToShow(searchBy?.value.toString() || '');
    }
  }, [filters]);

  // Debounce functionality with 1s delay
  useEffect(() => {
    // Clear existing debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set a new debounce timer
    debounceTimer.current = setTimeout(() => {
      const searchBy = filters?.find((filter) => filter.searchBy);
      const trimmedValue = valueToShow.toString().trim();
      if (filtersToDispatch && searchBy?.value.toString().trim() !== trimmedValue) {
        dispatch(
          filtersToDispatch({
            key: 'searchBy',
            value: trimmedValue || '',
            type: 'add',
          }),
        );
      }
    }, 1000); // 1 second

    // Cleanup on unmount or valueToShow change
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [valueToShow]);

  return (
    <div className="custom-table-header-wrap-v2" dir={i18n.dir()}>
      <div className={`custom-table-header-first-row ${flexDirection ? 'direction-reverse' : ''}`}>
        <div className="custom-table-left">
          {leftTopContent && <div className="custom-table-left-container">{leftTopContent}</div>}
        </div>
        <div className="custom-table-right-wrap">
          <div className="custom-table-right-content">
            {!flexDirection && <div className="custom-table-right-border" />}

            {filters && filtersToDispatch && (
              <div className="custom-table-header-filter-wrap">
                <CustomPopover
                  clickableElement={
                    <>
                      <MyButton
                        label=""
                        buttonType="none"
                        onClickFunc={() => {}}
                        svgIcon={<Filter />}
                        hoverLabel={t('filter')}
                      />
                      <div className="tag tag-danger">{countAppliedFilters()}</div>
                    </>
                  }
                  version="v2"
                  popOverContent={
                    <CustomTableFilterV2
                      filters={filters}
                      filterListLoading={filterListLoading}
                      filtersToDispatch={filtersToDispatch}
                      filterListToDispatch={filterListToDispatch}
                    />
                  }
                />
              </div>
            )}
            {filters && filtersToDispatch && (
              <div className="custom-table-filter-clear">
                <MyButton
                  label=""
                  buttonType="none"
                  onClickFunc={handleClearFilters}
                  svgIcon={<ClearFilter />}
                  hoverLabel={t('clearFilter')}
                />
              </div>
            )}
            {hidedColumn && setHidedColumn && labelForHideColumn && (
              <div className="hided-colum-data">
                <TableFieldAllow
                  labelForHideColumn={labelForHideColumn}
                  hidedColumn={hidedColumn}
                  setHidedColumn={setHidedColumn}
                  hoverLabel={t('columnHide')}
                />
              </div>
            )}
            <div className="reset-button-data">
              <MyButton
                label=""
                buttonType="none"
                onClickFunc={refreshCurrentPages}
                svgIcon={<ResetButton />}
                hoverLabel={t('refresh')}
              />
            </div>

            {sizeChangeButtonRequired && (
              <div className="custom-table-size-change">
                <MyButton
                  label=""
                  buttonType="primary-outline-none"
                  onClickFunc={handleChangeFilter}
                  svgIcon={handleFontChangeIcon()}
                  hoverLabel={t('textSize')}
                />
              </div>
            )}

            {exportDataPdfOrCSV && (
              <div className="export-data">
                <ExportButton
                  label=""
                  buttonList={[
                    {
                      key: 'pdf',
                      allow: true,
                      /**
                       * Export pdf function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('pdf'),
                    },
                    {
                      key: 'csv',
                      allow: true,
                      /**
                       * Export csv function
                       * @returns
                       */
                      onClickFunction: () => exportDataPdfOrCSV('csv'),
                    },
                  ]}
                  disabled={exportData?.length === 0}
                  loader={exportLoader}
                  version="v2"
                  hoverLabel={t('export')}
                />
              </div>
            )}
            {searchFilter ? (
              <div className="search">
                <ExpandSearchBar
                  direction={i18n.dir()}
                  value={valueToShow}
                  onChange={(value) => handleChangeFilters(searchFilter.key, value)}
                  placeholder={t(searchFilter.name)}
                />
              </div>
            ) : (
              <div className="search-select-content" />
            )}
            <div className="custom-table-right">
              {rightTopContent && (
                <div className="custom-table-right-container">{rightTopContent}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {filters?.filter((filter) => !filter.searchBy).some((filter) => filter.applyFilter) && (
        <div className="custom-table-header-second-row">
          {filters
            .filter((filter) => !filter.searchBy)
            .map((filter) => {
              let filterTag = null;
              if (
                filter.applyFilter &&
                (filter.key === filterType.DATE_RANGE || filter.key === filterType.TIME_RANGE)
              ) {
                filterTag = (
                  <div className="filter-tag-wrap date-range">
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details-date">
                      <p>{t(filter.name)}:</p>
                      <p>{filter.value1}</p>
                      <p>-</p>
                      <p>{filter.value2}</p>
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              } else if (
                filter.applyFilter &&
                filter.key === filterType.ANGLE_BRACKETS &&
                filter?.value1 &&
                filter?.value2
              ) {
                filterTag = (
                  <div className="filter-tag-wrap">
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details-date">
                      <p>{filter.value2}</p>
                      <p>{filter.value1}</p>
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              } else if (
                filter.applyFilter &&
                Array.isArray(filter?.value) &&
                filter.type === filterType.MULTISELECT
              ) {
                filterTag = (
                  <div className="filter-tag-wrap" key={filter.value.join(', ')}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details">
                      <h6>{t(filter.name)}:</h6>
                      {filter?.valueToShow ? <p>{filter?.valueToShow.join(', ')}</p> : <p />}
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              } else if (filter.applyFilter && Array.isArray(filter.value)) {
                filterTag = (
                  <div className="filter-tag-wrap" key={filter.value.join(', ')}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details">
                      <h6>{t(filter.name)}: </h6>
                      <p>{filter.value.join(', ')}</p>
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              } else if (filter.applyFilter && typeof filter.value === 'string') {
                filterTag = (
                  <div className="filter-tag-wrap" key={filter.value}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details">
                      <h6>{t(filter.name)}: </h6>
                      <p>
                        {filter.listOfValue?.find((item) => item.value === filter.value)?.label ||
                          (filter.valueToShow && filter.valueToShow.length > 0
                            ? filter.valueToShow[0]
                            : filter.value)}
                      </p>
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              }
              return filterTag;
            })}
        </div>
      )}
    </div>
  );
}

export default CustomTableHeaderV2;
